import { Checkbox } from '@mantine/core';
import Select from '@xver/components/Atoms/Select/Select';
import { useDisclosure } from '@mantine/hooks';
import { IconX } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BackupFrequency,
    BackupSetting,
    UpdateBackupSettingRequest,
} from '../../../../../@types/backup';
import { Button, ConfirmActionModal } from '../../../../../components/Atoms';
import DialogBox from '../../../../../components/Atoms/DialogBox/DialogBox';
import { useToggleBackupMutate } from '../../../../../hooks/CloudComputeHooks/useToggleBackupMutate';
import { useUpdateBackupSettingsMutate } from '../../../../../hooks/CloudComputeHooks/useUpdateBackupSettingsMutate';
import { computeNextRun, isoForHour } from './backupFormat';

interface AutomatedBackupsCardProps {
    instanceId: number;
    settings?: BackupSetting;
    /** False with view-only access: the schedule is shown read-only and cannot be disabled. */
    canManage: boolean;
}

function EnabledPill({ enabled }: { enabled?: boolean }) {
    const { t } = useTranslation('cloudComputeInstance');
    const [tint, color] = enabled
        ? ['#10b98126', 'var(--color-status-success)']
        : ['#9ca3af26', 'var(--color-status-neutral)'];
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs"
            style={{ backgroundColor: tint, color }}
        >
            <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: color }}
            />
            {enabled ? t('backup.enabled') : t('backup.disabled')}
        </span>
    );
}

function LegendItem({ color, label }: { color: string; label: string }) {
    return (
        <span className="inline-flex items-center gap-2 text-sm text-fg/80">
            <span
                className="inline-block w-3 h-3 rounded-full border-2"
                style={{ borderColor: color }}
            />
            {label}
        </span>
    );
}

function ColumnLabel({ children }: { children: string }) {
    return <div className="text-xs text-fg/68 uppercase tracking-wider mb-2">{children}</div>;
}

function deriveWeekdayFlags(now = new Date()) {
    const idx = now.getDay();
    return {
        on_sunday: idx === 0,
        on_monday: idx === 1,
        on_tuesday: idx === 2,
        on_wednesday: idx === 3,
        on_thursday: idx === 4,
        on_friday: idx === 5,
        on_saturday: idx === 6,
    };
}

export default function AutomatedBackupsCard({
    instanceId,
    settings,
    canManage,
}: AutomatedBackupsCardProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const toggleMutation = useToggleBackupMutate();
    const updateMutation = useUpdateBackupSettingsMutate();
    const [understood, setUnderstood] = useState(false);
    const [disableOpened, { open: openDisable, close: closeDisable }] = useDisclosure();

    const FREQUENCY_OPTIONS = useMemo<Array<{ value: BackupFrequency; label: string }>>(
        () => [
            { value: 'daily', label: t('backup.frequencyOptions.daily') },
            { value: 'weekly', label: t('backup.frequencyOptions.weekly') },
            { value: 'monthly', label: t('backup.frequencyOptions.monthly') },
        ],
        [t]
    );

    const HOUR_OPTIONS = useMemo(
        () =>
            Array.from({ length: 24 }, (_, h) => ({
                value: String(h),
                label: t('backup.hourFormat', { hour: String(h).padStart(2, '0') }),
            })),
        [t]
    );

    const RETENTION_OPTIONS = useMemo(
        () => [
            { value: '3', label: t('backup.retentionOptions.three') },
            { value: '4', label: t('backup.retentionOptions.four') },
            { value: '7', label: t('backup.retentionOptions.seven') },
        ],
        [t]
    );

    const currentHour = useMemo(() => {
        if (!settings?.starting_at) return 3;
        const d = new Date(settings.starting_at);
        return isNaN(d.getTime()) ? 3 : d.getHours();
    }, [settings?.starting_at]);

    const currentRetentionCount = useMemo(() => {
        const n = settings?.number_of_retention;
        if (n === 3 || n === 4 || n === 7) return String(n);
        return '4';
    }, [settings?.number_of_retention]);

    const isSaving = updateMutation.isPending;

    const buildPayload = (
        overrides: Partial<UpdateBackupSettingRequest>
    ): UpdateBackupSettingRequest => {
        const freq: BackupFrequency = (overrides.frequency ??
            settings?.frequency ??
            'weekly') as BackupFrequency;
        const hour = overrides.starting_at
            ? new Date(overrides.starting_at).getHours()
            : currentHour;
        const retentionCount = Number(overrides.number_of_retention ?? currentRetentionCount ?? 4);

        const base: UpdateBackupSettingRequest = {
            frequency: freq,
            starting_at: overrides.starting_at ?? isoForHour(hour),
            retention: 'week',
            number_of_retention: retentionCount,
        };

        if (freq === 'daily') {
            base.on_every_hour = 24;
        } else if (freq === 'weekly') {
            const weekday = deriveWeekdayFlags();
            base.on_monday = settings?.on_monday ?? weekday.on_monday;
            base.on_tuesday = settings?.on_tuesday ?? weekday.on_tuesday;
            base.on_wednesday = settings?.on_wednesday ?? weekday.on_wednesday;
            base.on_thursday = settings?.on_thursday ?? weekday.on_thursday;
            base.on_friday = settings?.on_friday ?? weekday.on_friday;
            base.on_saturday = settings?.on_saturday ?? weekday.on_saturday;
            base.on_sunday = settings?.on_sunday ?? weekday.on_sunday;
            const anyChecked = [
                base.on_monday,
                base.on_tuesday,
                base.on_wednesday,
                base.on_thursday,
                base.on_friday,
                base.on_saturday,
                base.on_sunday,
            ].some(Boolean);
            if (!anyChecked) base.on_sunday = true;
        } else if (freq === 'monthly') {
            base.on_day = settings?.on_day && settings.on_day > 0 ? settings.on_day : 1;
        }

        return base;
    };

    const handleScheduleChange = (value: string | null) => {
        if (!value) return;
        updateMutation.mutate({
            id: instanceId,
            payload: buildPayload({ frequency: value as BackupFrequency }),
        });
    };

    const handleHourChange = (value: string | null) => {
        if (value == null) return;
        updateMutation.mutate({
            id: instanceId,
            payload: buildPayload({ starting_at: isoForHour(Number(value)) }),
        });
    };

    const handleRetentionChange = (value: string | null) => {
        if (!value) return;
        updateMutation.mutate({
            id: instanceId,
            payload: buildPayload({ number_of_retention: Number(value) }),
        });
    };

    const handleConfirmDisable = () => {
        toggleMutation.mutate(instanceId, {
            onSuccess: () => {
                closeDisable();
                setUnderstood(false);
            },
        });
    };

    return (
        <DialogBox
            label={t('backup.automatedTitle')}
            description={t('backup.automatedDescription')}
            rightDescription={<EnabledPill enabled={settings?.enabled} />}
            classNames="p-4 gap-4"
        >
            <p className="text-sm text-fg/68">{t('backup.automatedBlurb')}</p>

            <div className="flex flex-row flex-wrap gap-x-6 gap-y-2">
                <LegendItem color="#f59e0b" label={t('backup.legend.inProgress')} />
                <LegendItem color="#ef4444" label={t('backup.legend.deleting')} />
                <LegendItem color="#10b981" label={t('backup.legend.completed')} />
            </div>

            <div className="w-full border border-line rounded-md p-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-6">
                <div>
                    <ColumnLabel>{t('backup.schedule')}</ColumnLabel>
                    <Select
                        aria-label={t('backup.schedule')}
                        data={FREQUENCY_OPTIONS}
                        value={settings?.frequency ?? 'weekly'}
                        onChange={handleScheduleChange}
                        disabled={!canManage}
                        readOnly={isSaving}
                        allowDeselect={false}
                    />
                </div>
                <div>
                    <ColumnLabel>{t('backup.runAt')}</ColumnLabel>
                    <Select
                        aria-label={t('backup.runAt')}
                        data={HOUR_OPTIONS}
                        value={String(currentHour)}
                        onChange={handleHourChange}
                        disabled={!canManage}
                        readOnly={isSaving}
                        allowDeselect={false}
                    />
                </div>
                <div>
                    <ColumnLabel>{t('backup.retention')}</ColumnLabel>
                    <Select
                        aria-label={t('backup.retention')}
                        data={RETENTION_OPTIONS}
                        value={currentRetentionCount}
                        onChange={handleRetentionChange}
                        disabled={!canManage}
                        readOnly={isSaving}
                        allowDeselect={false}
                    />
                </div>
                <div>
                    <ColumnLabel>{t('backup.nextRun')}</ColumnLabel>
                    <div className="text-sm text-fg pt-2">{computeNextRun(settings)}</div>
                </div>
            </div>

            {canManage && (
                <div className="w-full flex flex-row justify-between items-center gap-4">
                    <Checkbox
                        label={t('backup.understandBilling')}
                        checked={understood}
                        onChange={(e) => setUnderstood(e.currentTarget.checked)}
                    />
                    <Button
                        variant="danger"
                        leftIcon={<IconX size={16} />}
                        onClick={openDisable}
                        disabled={!understood || toggleMutation.isPending}
                    >
                        {t('backup.disableBackups')}
                    </Button>
                </div>
            )}

            <ConfirmActionModal
                opened={disableOpened}
                onClose={closeDisable}
                title={t('backup.disableBackupsTitle')}
                message={t('backup.disableBackupsMessage')}
                confirmLabel={t('backup.disableBackups')}
                confirmVariant="danger"
                isLoading={toggleMutation.isPending}
                onConfirm={handleConfirmDisable}
            />
        </DialogBox>
    );
}
